import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Targetable filter controller
// Handles adding/removing filters for targeted content (system messages, attachments, etc.)
//
// Usage:
//   div data-controller="targetable"
//     a.add-target data-action="click->targetable#add"
//                  data-targetable-url-value="..."
//                  data-targetable-container-value="..."
//     .targets-container
//       .targetable-fields
//         a.remove-target data-action="click->targetable#remove"
//         select.model-select data-action="change->targetable#modelChange"
//
export default class extends Controller {
  static targets = ["container"]
  static values = { emptyStateText: String, fieldName: String }

  // Helper to safely parse JSON data attributes
  parseDataAttribute(value) {
    if (!value || value === '') return null
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  // Helper to build URLSearchParams with array support
  buildParams(data) {
    const params = new URLSearchParams()

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined || value === '') continue

      if (Array.isArray(value)) {
        // Send arrays as multiple params with [] suffix
        value.forEach(item => params.append(`${key}[]`, item))
      } else if (typeof value === 'object') {
        // Send objects as JSON strings
        params.append(key, JSON.stringify(value))
      } else {
        params.append(key, value)
      }
    }

    return params
  }

  add(event) {
    event.preventDefault()
    const button = event.currentTarget

    // Clear container if no fields exist yet
    const containerElement = document.getElementById(button.dataset.container)
    if (!containerElement.querySelector('.targetable-fields')) {
      containerElement.innerHTML = ''
    }

    // Remove empty state message if it exists
    const emptyState = containerElement.querySelector('.text-muted.text-center')
    if (emptyState) {
      emptyState.remove()
    }

    // Remove empty marker hidden field if it exists
    const emptyMarker = containerElement.querySelector('.targetable-empty-marker')
    if (emptyMarker) {
      emptyMarker.remove()
    }

    // Parse data attributes
    const availableModels = this.parseDataAttribute(button.dataset.availableModels)
    const collectionParams = this.parseDataAttribute(button.dataset.collectionParams)

    // Prepare data for request
    const data = this.buildParams({
      object_name: button.dataset.objectName,
      model: button.dataset.model || '',
      available_models: availableModels,
      parameter_collection_params: collectionParams,
      container: button.dataset.container
    })

    // Update object name with new timestamp for next click
    const currentObjectName = button.dataset.objectName
    const newObjectName = currentObjectName.replace(/(.*]\[)(\d+)(])$/, `$1${Date.now()}$3`)
    button.dataset.objectName = newObjectName

    // Fetch new target field via AJAX
    fetch(`${button.dataset.url}?${data.toString()}`, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/javascript, application/javascript, */*'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(text => {
      // Execute the JavaScript response
      const script = document.createElement('script')
      script.text = text
      document.body.appendChild(script)
      document.body.removeChild(script)
    })
    .catch(error => {
      logger.error('Error adding target:', error)
    })
  }

  remove(event) {
    event.preventDefault()
    const button = event.currentTarget
    const form = button.closest('.targetable-fields')
    const container = form.parentElement

    form.remove()

    // If no more fields, show empty state message and add hidden marker
    if (!container.querySelector('.targetable-fields')) {
      const emptyStateText = this.emptyStateTextValue || 'לא הוגדרו סינונים'
      const fieldName = this.fieldNameValue

      // Add hidden field to signal empty targets to the server
      if (fieldName) {
        const hiddenField = document.createElement('input')
        hiddenField.type = 'hidden'
        hiddenField.name = `${fieldName}[_empty]`
        hiddenField.value = '1'
        hiddenField.className = 'targetable-empty-marker'
        container.appendChild(hiddenField)
      }

      const emptyState = document.createElement('div')
      emptyState.className = 'text-muted text-center py-3'
      emptyState.textContent = emptyStateText
      container.appendChild(emptyState)
    }
  }

  modelChange(event) {
    const select = event.currentTarget
    const form = select.closest('.targetable-fields')

    // Clone the form to capture current values
    const clonedForm = form.cloneNode(true)

    // Get all inputs from both forms
    const inputSelector = 'select, textarea, input'
    const originalInputs = form.querySelectorAll(inputSelector)
    const clonedInputs = clonedForm.querySelectorAll(inputSelector)

    // Copy current values and update names
    clonedInputs.forEach((item, index) => {
      item.value = originalInputs[index].value
      item.name = item.name.replace(select.dataset.objectName, 'target')
    })

    // Serialize cloned form data
    const formData = new FormData()
    clonedInputs.forEach(input => {
      if (input.name && input.value !== undefined) {
        formData.append(input.name, input.value)
      }
    })

    // Parse data attributes
    const availableModels = this.parseDataAttribute(select.dataset.availableModels)
    const collectionParams = this.parseDataAttribute(select.dataset.collectionParams)

    // Prepare URL parameters
    const params = this.buildParams({
      object_name: select.dataset.objectName,
      parameter_collection_params: collectionParams,
      model: select.dataset.model || '',
      available_models: availableModels
    })

    // Append form data to params
    for (const [key, value] of formData) {
      params.append(key, value)
    }

    // Fetch updated fields
    fetch(`${select.dataset.url}?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/javascript, application/javascript, */*'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(text => {
      // Execute the JavaScript response
      const script = document.createElement('script')
      script.text = text
      document.body.appendChild(script)
      document.body.removeChild(script)
    })
    .catch(error => {
      logger.error('Error updating model:', error)
    })
  }
}
